import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import {
  invoicesApi,
  InvoiceListParams,
  CreateArtistInvoiceParams,
  CreateOrderInvoiceParams,
} from '../api/invoicesApi'

/**
 * Fetch paginated list of invoices
 */
export function useInvoices(params: InvoiceListParams = {}) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => invoicesApi.getInvoices(params),
  })
}

/**
 * Fetch single invoice with items
 */
export function useInvoice(id: number | null) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.getInvoice(id!),
    enabled: id !== null,
  })
}

/**
 * Fetch monthly summary for artist invoice generation
 */
export function useMonthlySummary(artistId?: number) {
  return useQuery({
    queryKey: ['invoices', 'monthly-summary', artistId],
    queryFn: () => invoicesApi.getMonthlySummary(artistId),
  })
}

/**
 * Create artist sales invoice mutation
 */
export function useCreateArtistInvoice() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (params: CreateArtistInvoiceParams) =>
      invoicesApi.createArtistSalesInvoice(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast({
        title: 'Invoice Created',
        description: `Invoice ${data.invoice.invoiceNumber} has been created successfully.`,
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create invoice',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Create order invoice mutation (Manager only)
 */
export function useCreateOrderInvoice() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (params: CreateOrderInvoiceParams) =>
      invoicesApi.createOrderInvoice(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast({
        title: 'Invoice Created',
        description: `Invoice ${data.invoice.invoiceNumber} has been created successfully.`,
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create invoice',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Update invoice status mutation
 */
export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      invoicesApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast({
        title: 'Status Updated',
        description: 'Invoice status has been updated.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update status',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Regenerate PDF mutation
 */
export function useRegeneratePdf() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => invoicesApi.regeneratePdf(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      toast({
        title: 'PDF Regenerated',
        description: 'The invoice PDF has been regenerated.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to regenerate PDF',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Download invoice PDF
 */
export function useDownloadInvoice() {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (id: number) => {
      const url = await invoicesApi.getDownloadUrl(id)
      window.open(url, '_blank')
      return url
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to download invoice',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete invoice mutation
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => invoicesApi.deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast({
        title: 'Invoice Deleted',
        description: 'The invoice has been deleted.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete invoice',
        variant: 'destructive',
      })
    },
  })
}
